import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ArrowRight, CalendarClock, CheckSquare, ChevronDown, FileText, Globe,
  Handshake, LogOut, Menu, MessageCircle, MoreHorizontal, Package, Plus, Presentation, Radio, RotateCcw,
  Frown, Search, Settings, ShieldCheck, Target, Ticket, TrendingUp,
  UserRound, Users, Wallet, X, Zap,
} from "lucide-react";

import { useAuth, canAccessTrash } from "@/lib/auth";
import { AskSaarv } from "@/components/revenueos/AskSaarv";
import { CommandPalette } from "@/components/revenueos/CommandPalette";
import { QuickAdd } from "@/components/revenueos/QuickAdd";
import { initials } from "@/lib/utils-ros";
import { Toaster } from "@/components/ui/sonner";

const PROCESS_ALL = [
  { to: "/universe", label: "Universe", short: "Universe", icon: Globe, testid: "stage-universe", perm: "universe" },
  { to: "/signals", label: "Signals", short: "Signals", icon: Radio, testid: "stage-signals", perm: "signals" },
  { to: "/leads", label: "Leads", short: "Leads", icon: Zap, testid: "stage-leads", perm: "leads" },
  { to: "/opportunities", label: "Opportunities", short: "Opps", icon: Target, testid: "stage-opps", perm: "opportunities" },
  { to: "/orders", label: "Orders", short: "Orders", icon: Package, testid: "stage-orders", perm: "orders" },
  { to: "/tickets", label: "Tickets", short: "Tickets", icon: Ticket, testid: "stage-tickets", perm: "customers" },
  { to: "/reorder", label: "Reorder", short: "Reorder", icon: RotateCcw, testid: "stage-reorder", perm: "orders" },
];

const TASK_ITEMS = [
  { to: "/tasks", label: "To-Do", description: "Tasks and commitments", icon: CheckSquare, testid: "tool-tasks", perm: "customers" },
  { to: "/meetings", label: "Meetings", description: "Calls, visits and follow-ups", icon: CalendarClock, testid: "tool-meetings", perm: "customers" },
];

const PEOPLE_ITEMS = [
  { to: "/partners", label: "Partners", description: "People across partner firms", icon: Users, testid: "tool-partners" },
  { to: "/team", label: "Ergoniq Team", description: "Founders, marketing, Dev Ops and interns", icon: UserRound, testid: "tool-team" },
];

// Visible to every logged-in role — no `perm` gate, same as PEOPLE_ITEMS above.
const REPOSITORY_ITEMS = [
  { to: "/repositories/customer-presentations", label: "Customer Presentations", description: "Decks presented to customers", icon: Presentation, testid: "tool-repo-customer-presentations" },
  { to: "/repositories/technical-proposals", label: "Technical Proposals", description: "Technical proposal documents", icon: FileText, testid: "tool-repo-technical-proposals" },
  { to: "/repositories/commercial-proposals", label: "Commercial Proposals", description: "Commercial proposal documents", icon: Handshake, testid: "tool-repo-commercial-proposals" },
];

export default function Layout() {
  const { user, logout, can, canWrite, canEditOpportunities, role } = useAuth();
  const [askOpen, setAskOpen] = useState(false);
  const [askSeed, setAskSeed] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const loc = useLocation();

  const PROCESS = PROCESS_ALL.filter(item => !item.perm || can(item.perm));
  const TASKS = TASK_ITEMS.filter(item => !item.perm || can(item.perm));
  const canDecideApprovals = role === "super_admin" || role === "admin" || role === "founder";
  const canUseQuickAdd = canWrite("signals") || canWrite("leads") || canEditOpportunities() || canWrite("orders");
  const showTrash = canAccessTrash(user);
  const showAdminMenu = role === "super_admin" || role === "admin" || role === "founder" || role === "growth_marketing";

  const ADMIN_ITEMS = [
    { to: "/admin", label: "User administration", description: "Users, roles and access", icon: ShieldCheck, testid: "admin-users", show: role === "super_admin" || role === "admin" },
    { to: "/partner-management", label: "Partner management", description: "PMM, firms and partner controls", icon: Users, testid: "admin-partners", show: can("partner_assignments") },
    { to: "/marketing", label: "Marketing insights", description: "Campaign and demand intelligence", icon: TrendingUp, testid: "tool-marketing", show: can("marketing") },
    { to: "/finance", label: "Finance", description: "Revenue and payment controls", icon: Wallet, testid: "tool-finance", show: can("finance") },
    { to: "/approvals", label: "Approvals", description: "Executive decision queue", icon: CheckSquare, testid: "tool-approvals", show: can("approvals") },
    { to: "/audit", label: "Audit log", description: "Admin activity history", icon: MoreHorizontal, testid: "tool-audit", show: can("audit_log") },
  ].filter(item => item.show);

  const openAsk = (seed = "") => { setAskSeed(seed); setAskOpen(true); };
  if (typeof window !== "undefined") window.__openAskSaarv = openAsk;

  function toggleMenu(menu) {
    setProfileOpen(false);
    setOpenMenu(current => current === menu ? "" : menu);
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <header className="sticky top-0 z-40 bg-white border-b border-black/10 shadow-[0_5px_18px_rgba(7,83,154,0.05)]" data-testid="top-bar">
        <div className="h-[72px] px-4 md:px-7 grid grid-cols-[auto_1fr_auto] md:grid-cols-[minmax(260px,1fr)_minmax(320px,520px)_minmax(260px,1fr)] items-center gap-3 md:gap-6">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu"
              className="md:hidden text-[#07539A] hover:text-[#00A2D4]">
              <Menu size={21} />
            </button>
            <Link to="/" className="flex items-center gap-3 shrink-0 min-w-0" data-testid="home-logo">
              <img src="/saarv-logo.png" alt="Saarv" className="h-10 md:h-11 w-auto" />
              <div className="hidden sm:flex flex-col leading-none pl-3 border-l border-black/10">
                <div className="font-display text-[13px] tracking-[0.16em] text-[#07539A]">SaarvOS</div>
                <div className="text-[9px] uppercase tracking-[0.12em] text-[#8AA0AC] mt-1.5">Rise with knowledge</div>
              </div>
            </Link>
          </div>

          <button type="button" onClick={() => setSearchOpen(true)} data-testid="global-search-btn" title="Search everything (⌘K)"
            className="hidden sm:flex w-full h-11 px-4 rounded-xl border border-[#BFD3DD] bg-[#F9FCFD] hover:bg-white hover:border-[#00A2D4] hover:shadow-[0_5px_14px_rgba(7,83,154,0.08)] text-[#607D8B] items-center gap-3 transition-all text-left">
            <Search size={17} className="text-[#07539A] shrink-0" />
            <span className="flex-1 text-[13px]">Search</span>
            <kbd className="text-[10px] font-mono border border-[#C9DCE5] rounded-md px-2 py-1 text-[#78909C] bg-white">⌘ K</kbd>
          </button>

          <div className="relative justify-self-end" onBlur={() => window.setTimeout(() => setProfileOpen(false), 160)}>
            <button type="button" onClick={() => { setOpenMenu(""); setProfileOpen(current => !current); }} data-testid="user-menu" aria-label="Open profile menu"
              className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-[#07539A] text-[#FFFFFF] overflow-hidden flex items-center justify-center text-[12px] font-mono font-semibold shadow-[0_6px_14px_rgba(7,83,154,0.22)] hover:bg-[#063F73] hover:-translate-y-0.5 transition-all">
              {user?.avatar_url
                ? <img src={user.avatar_url} alt={user.name || "Profile"} className="w-full h-full object-cover" />
                : initials(user?.name || "")}
            </button>
            {profileOpen && (
              <div className="absolute right-0 top-[3.25rem] w-64 rounded-xl border border-[#D4E2E8] bg-white shadow-[0_18px_44px_rgba(7,83,154,0.18)] p-2 z-50" data-testid="profile-menu">
                <div className="px-3 py-3 border-b border-black/[0.07]">
                  <div className="text-[13px] font-semibold text-[#102A43] truncate">{user?.name}</div>
                  <div className="text-[10px] text-[#78909C] truncate mt-0.5">{user?.email}</div>
                  <div className="mt-2 inline-flex px-2 py-0.5 rounded-full bg-[#E5F3F9] text-[#07539A] text-[9px] uppercase tracking-wider">{String(role || "user").replaceAll("_", " ")}</div>
                </div>
                <Link to="/settings" onClick={() => setProfileOpen(false)} data-testid="profile-settings"
                  className="mt-1 flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] text-[#102A43] hover:bg-[#EDF7FB]">
                  <Settings size={14} className="text-[#07539A]" /> User settings
                </Link>
                <button type="button" onClick={logout} data-testid="logout-btn"
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] text-[#B42318] hover:bg-[#FFF1F0]">
                  <LogOut size={14} /> Log out
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex h-[58px] items-center px-7 border-t border-black/[0.06] gap-2">
          <HeaderMenu label="Tasks" menuKey="tasks" openMenu={openMenu} onToggle={toggleMenu} items={TASKS} location={loc} testId="tasks-menu-btn" />
          <HeaderMenu label="People" menuKey="people" openMenu={openMenu} onToggle={toggleMenu} items={PEOPLE_ITEMS} location={loc} testId="people-menu-btn" />
          {showAdminMenu && (
            <HeaderMenu label="Admin" menuKey="admin" openMenu={openMenu} onToggle={toggleMenu} items={ADMIN_ITEMS} location={loc} testId="admin-menu-btn" />
          )}
          <HeaderMenu label="Repositories" menuKey="repositories" openMenu={openMenu} onToggle={toggleMenu} items={REPOSITORY_ITEMS} location={loc} testId="repositories-menu-btn" />

          <div className="ml-auto flex items-center gap-2.5">
            <Link to="/chat" data-testid="ask-niq-btn" title="Ask Niq — intelligence and CRM actions"
              className={`h-10 px-6 rounded-full flex items-center gap-2 text-[13px] font-semibold shadow-[0_6px_14px_rgba(22,135,234,0.20)] hover:-translate-y-0.5 transition-all ${loc.pathname.startsWith("/chat") ? "bg-[#07539A] text-[#FFFFFF]" : "bg-[#1687EA] text-[#FFFFFF] hover:bg-[#0875D1]"}`}>
              <MessageCircle size={17} /> Ask Niq
            </Link>
          </div>
        </div>

        <nav className="hidden md:block mx-5 md:mx-7 mb-3 rounded-2xl border border-[#07539A] bg-[#E7F5FB] shadow-[0_9px_22px_rgba(7,83,154,0.12)] overflow-hidden" data-testid="process-bar">
          <div className="flex items-center px-3 py-1 min-h-[44px]">
            <div className="flex-1 min-w-0 flex items-center overflow-hidden">
              {PROCESS.map((item, index) => {
                const active = loc.pathname.startsWith(item.to);
                return (
                  <div key={item.to} className="flex items-center shrink-0">
                    <NavLink to={item.to} data-testid={item.testid}
                      className={`relative px-1.5 lg:px-3 py-1.5 rounded-lg text-[12px] lg:text-[13px] font-medium transition-all ${active ? "bg-[#D4ECF6] text-[#101820] shadow-[inset_0_0_0_1px_rgba(7,83,154,0.14)]" : "text-[#101820] hover:bg-[#DDF0F8]"}`}>
                      <span className="lg:hidden">{item.short}</span>
                      <span className="hidden lg:inline">{item.label}</span>
                      {active && <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full bg-[var(--copper)]" />}
                    </NavLink>
                    {index < PROCESS.length - 1 && <ArrowRight size={14} className="mx-0.5 text-[#E28A23]" strokeWidth={2.5} />}
                  </div>
                );
              })}
            </div>
            <div className="relative z-10 ml-2 pl-3 border-l border-[#AFCEDC] bg-[#E7F5FB] flex items-center gap-2 shrink-0">
              {showTrash && (
              <NavLink
                to="/trash"
                data-testid="process-lost-btn"
                title="View lost and discarded pipeline records"
                aria-label="View lost and discarded pipeline records"
                className={({ isActive }) => `h-8 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-1.5 transition-all ${isActive ? "border-[#07539A] bg-[#D4ECF6] text-[#07539A]" : "border-[#AFCEDC] bg-white/80 text-[#243B53] hover:border-[#07539A] hover:text-[#07539A]"}`}
              >
                <Frown size={14} /> Lost
              </NavLink>
              )}
              {canUseQuickAdd && (
                <button
                  type="button"
                  onClick={() => setQuickAddOpen(true)}
                  data-testid="quick-add-btn"
                  title="Add entity at any stage"
                  className="h-8 px-3 rounded-lg border border-[#07539A] bg-[#07539A] text-[#FFFFFF] hover:bg-[#063F73] text-[12px] font-medium flex items-center gap-1.5 transition-all"
                >
                  <Plus size={14} /> Add
                </button>
              )}
            </div>
          </div>
        </nav>

        <div className="md:hidden px-4 pb-3 flex gap-2">
          <button type="button" onClick={() => setSearchOpen(true)} data-testid="mobile-search-btn"
            className="h-10 w-10 rounded-xl border border-[#C9DCE5] text-[#07539A] flex items-center justify-center"><Search size={17} /></button>
          <Link to="/chat" data-testid="mobile-ask-niq" className="flex-1 h-10 rounded-full bg-[#1687EA] text-[#FFFFFF] flex items-center justify-center gap-2 text-[12px] font-semibold">
            <MessageCircle size={15} /> Ask Niq
          </Link>
        </div>
      </header>

      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={() => setDrawerOpen(false)}>
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white border-r border-black/10 flex flex-col" onClick={event => event.stopPropagation()} data-testid="mobile-drawer">
            <div className="p-4 flex items-center justify-between border-b border-black/10">
              <img src="/saarv-logo.png" alt="Saarv" className="h-9 w-auto" />
              <button type="button" onClick={() => setDrawerOpen(false)} data-testid="drawer-close" className="text-[#607D8B]"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              <MobileSection label="Revenue Process" items={PROCESS} onSelect={() => setDrawerOpen(false)} />
              <MobileSection label="Tasks" items={TASKS} onSelect={() => setDrawerOpen(false)} />
              <MobileSection label="People" items={PEOPLE_ITEMS} onSelect={() => setDrawerOpen(false)} />
              {showAdminMenu && <MobileSection label="Admin" items={ADMIN_ITEMS} onSelect={() => setDrawerOpen(false)} />}
              <MobileSection label="Repositories" items={REPOSITORY_ITEMS} onSelect={() => setDrawerOpen(false)} />
            </div>
            <div className="p-4 border-t border-black/10">
              <div className="text-[12px] font-semibold text-[#102A43]">{user?.name}</div>
              <div className="text-[10px] text-[#78909C] uppercase tracking-wider mt-0.5">{String(role || "user").replaceAll("_", " ")}</div>
              <button type="button" onClick={logout} className="mt-3 w-full h-9 rounded-lg bg-[#FFF1F0] text-[#B42318] flex items-center justify-center gap-2 text-[11px]"><LogOut size={13} /> Log out</button>
            </div>
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-x-hidden" data-testid="main-content"><Outlet /></main>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
      <AskSaarv open={askOpen} onOpenChange={(open) => { setAskOpen(open); if (!open) setAskSeed(""); }} seedMessage={askSeed} />
      <QuickAdd open={quickAddOpen} onOpenChange={setQuickAddOpen} />
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
}

function HeaderMenu({ label, menuKey, openMenu, onToggle, items, location, testId }) {
  const open = openMenu === menuKey;
  const active = items.some(item => item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to));
  return (
    <div className="relative">
      <button type="button" onClick={() => onToggle(menuKey)} data-testid={testId}
        className={`h-10 px-4 rounded-xl flex items-center gap-2 text-[13px] font-semibold transition-colors ${active || open ? "bg-[#E7F5FB] text-[#07539A]" : "text-[#07539A] hover:bg-[#F0F7FA]"}`}>
        {label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-12 w-72 rounded-xl border border-[#D5E3E9] bg-white p-2 shadow-[0_18px_44px_rgba(7,83,154,0.18)] z-50" data-testid={`${menuKey}-dropdown`}>
          {items.map(item => (
            <Link key={item.to} to={item.to} data-testid={item.testid} onClick={() => onToggle("")}
              className="group flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#EDF7FB] transition-colors">
              <span className="w-9 h-9 rounded-lg bg-[#E6F4FA] text-[#07539A] flex items-center justify-center shrink-0 group-hover:bg-white"><item.icon size={15} /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-medium text-[#102A43]">{item.label}</span>
                <span className="block text-[9px] text-[#78909C] truncate mt-0.5">{item.description}</span>
              </span>
              <ArrowRight size={12} className="text-[#A1B4BD] group-hover:text-[#00A2D4]" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileSection({ label, items, onSelect }) {
  return (
    <div className="mt-2 first:mt-0">
      <div className="px-4 py-2 text-[9px] uppercase tracking-widest text-[#78909C]">{label}</div>
      {items.map(item => (
        <NavLink key={item.to} to={item.to} onClick={onSelect} data-testid={`m-${item.testid}`}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-[12px] ${isActive ? "bg-[#E7F5FB] text-[#07539A] border-l-2 border-[var(--copper)] font-semibold" : "text-[#102A43] hover:bg-[#F4F8FA]"}`}>
          <item.icon size={14} className="text-[#07539A]" /> {item.label}
        </NavLink>
      ))}
    </div>
  );
}
